import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isExpanded } from 'bpmn-js/lib/util/DiUtil';
import { ensureLayout } from '../utils/ensureLayout';
import { alleIds } from '../utils/bausteine';

const CONNECTION_TYPES = new Set([
  'bpmn:SequenceFlow',
  'bpmn:MessageFlow',
  'bpmn:Association'
]);

/**
 * Adds a "Sub-process (from file)" entry to the replace (wrench) popup menu.
 *
 * When triggered it opens a file picker for a .bpmn/.xml file, replaces the
 * selected element with a collapsed sub-process and imports the flow elements
 * from the chosen file into the new sub-process.
 *
 * Version history attributes (custom:version, custom:author, custom:lastModified,
 * custom:versionHistory) from the imported file are intentionally discarded —
 * only flowElements + DI from the source process are transferred.
 */
export default function SubProcessFromFileProvider(injector) {
  this._injector = injector;

  const popupMenu = injector.get('popupMenu');
  popupMenu.registerProvider('bpmn-replace', 500, this);
}

SubProcessFromFileProvider.$inject = ['injector'];


// ---------------------------------------------------------------------------
// Popup menu integration
// ---------------------------------------------------------------------------

/**
 * Return an updater function that inserts our custom entry right after
 * "Sub-process (collapsed)" in the replace menu.
 */
SubProcessFromFileProvider.prototype.getPopupMenuEntries = function (target) {
  const translate = this._injector.get('translate');

  return (entries) => {
    if (!isApplicable(target)) return entries;

    const entry = {
      label: translate('Sub-process (from file)'),
      className: 'bpmn-icon-subprocess-collapsed',
      action: () => this._importFromFile(target)
    };

    return insertAfterKey(
      entries,
      'replace-with-collapsed-subprocess',
      'replace-with-subprocess-from-file',
      entry
    );
  };
};


// ---------------------------------------------------------------------------
// Import orchestration
// ---------------------------------------------------------------------------

/**
 * Open a file picker, parse the selected BPMN file, replace the target element
 * with a collapsed sub-process, and import the flow elements into it.
 */
SubProcessFromFileProvider.prototype._importFromFile = function (target) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.bpmn,.xml';

  input.onchange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await this._doImport(target, await file.text());
    } catch (error_) {
      console.error('[SubProcessFromFile] Import failed:', error_);
      alert('Import fehlgeschlagen: ' + error_.message);
    }
  };

  input.click();
};

/**
 * Core import logic:
 * 1. Parse the imported XML
 * 2. Replace target with collapsed sub-process
 * 3. Recursively import flow elements + DI into the new sub-process
 */
SubProcessFromFileProvider.prototype._doImport = async function (target, xml) {
  const moddle = this._injector.get('moddle');

  // 0. Auto-generate layout if the file has no DI
  const layoutXml = await ensureLayout(xml);

  // 1. Parse imported XML (versions / history on bpmn:Definitions are ignored)
  const { rootElement: sourceDefs } = await moddle.fromXML(layoutXml, 'bpmn:Definitions');
  const sourceProcess = findSourceProcess(sourceDefs);
  if (!sourceProcess) throw new Error('Die Datei enthält keinen BPMN-Prozess.');

  return importProcessAsSubProcess(target, sourceDefs, sourceProcess, this._injector);
};

/**
 * Einen Prozess als eingeklappten Sub-Prozess an die Stelle von `target`
 * setzen — Elemente und Anordnung werden übernommen.
 *
 * Herausgelöst aus `_doImport`, damit die Bausteine denselben Weg gehen: Beim
 * Einfügen als **Kopie** kommt der Quellprozess nicht aus einer Datei,
 * sondern aus einer frisch geparsten Fassung derselben definitions. Die
 * Elemente sind dann garantiert eigene Objekte und nicht dieselben, an denen
 * der Baustein hängt.
 *
 * @param {object} target        zu ersetzendes Element
 * @param {object} sourceDefs    definitions, aus denen kopiert wird
 * @param {object} sourceProcess der Prozess darin
 * @param {object} injector
 */
export async function importProcessAsSubProcess(target, sourceDefs, sourceProcess, injector) {
  const bpmnReplace = injector.get('bpmnReplace');
  const modeling = injector.get('modeling');
  const canvas = injector.get('canvas');
  const elementRegistry = injector.get('elementRegistry');
  const eventBus = injector.get('eventBus');

  const flowElements = sourceProcess.flowElements || [];
  if (flowElements.length === 0) throw new Error('Der importierte Prozess enthält keine Elemente.');

  // 2. Build DI lookup (object-ref keyed) from ALL diagrams in the file
  const diMap = buildDiMap(sourceDefs);

  // 3. Remap conflicting IDs — gegen ALLE IDs der Zieldatei, nicht nur gegen
  //    die Elementregistrierung.
  const zielDefs = getDefinitions(canvas);
  const belegt = alleIds(zielDefs);
  elementRegistry.forEach((el) => { if (el.id) belegt.add(el.id); });
  remapIds(flowElements, belegt, diMap);

  // 4. Replace target with collapsed sub-process
  const processName = sourceProcess.name || removeExtension(sourceProcess.id);
  const newShape = bpmnReplace.replaceElement(target, {
    type: 'bpmn:SubProcess',
    isExpanded: false
  });
  modeling.updateProperties(newShape, { name: processName });

  // 5. Find the plane that SubProcessPlaneBehavior created automatically
  const planeId = newShape.id + '_plane';
  const subProcessPlane = canvas.getRootElements().find(r => r.id === planeId);
  if (!subProcessPlane) {
    console.warn('[SubProcessFromFile] Plane not found:', planeId);
    return;
  }

  // 6. Recursively import elements into the sub-process
  importElementsToPlane(flowElements, newShape.businessObject, subProcessPlane, diMap, injector);

  // 7. Trigger drill-down overlays for all collapsed sub-processes
  //    DrilldownOverlayBehavior listens on commandStack events (not canvas),
  //    so canvas.addShape() alone won't create the "open" overlay.
  //    Firing import.render.complete causes it to scan all elements and add overlays.
  eventBus.fire('import.render.complete');

  // 8. Auto-colour imported elements if the source file had no colours
  if (!hasAnyColor(flowElements, diMap)) {
    applyDefaultColors(flowElements, elementRegistry, modeling);
  }

  eventBus.fire('elements.changed', { elements: [newShape] });
  console.log(
    '[SubProcessFromFile] Imported', flowElements.length,
    'elements from "' + processName + '"'
  );
}


// ---------------------------------------------------------------------------
// Recursive import engine
// ---------------------------------------------------------------------------

/**
 * Import flow elements into a sub-process plane. Handles nested collapsed
 * sub-processes by creating their planes and recursing into them.
 */
function importElementsToPlane(flowElements, parentBO, plane, diMap, injector) {
  const canvas = injector.get('canvas');
  const elementRegistry = injector.get('elementRegistry');

  // Separate nodes from connections
  const nodes = [];
  const connections = [];
  for (const el of flowElements) {
    (CONNECTION_TYPES.has(el.$type) ? connections : nodes).push(el);
  }

  // Ensure incoming/outgoing arrays exist (canvas.addShape expects them)
  for (const node of nodes) {
    if (!node.incoming) node.incoming = [];
    if (!node.outgoing) node.outgoing = [];
  }

  // Attach semantic model: set $parent and add to parent's flowElements
  attachToParentBO(flowElements, parentBO);

  // Attach DI elements to the correct BPMNPlane
  attachDiToPlane(nodes, connections, diMap, canvas, parentBO);

  // Create visual shapes & connections on the canvas plane
  const previousRoot = canvas.getRootElement();
  canvas.setRootElement(plane);

  try {
    const created = createShapes(nodes, plane, diMap, canvas, elementRegistry);
    createConnections(connections, plane, diMap, created, canvas, elementRegistry);

    // Recurse into nested collapsed sub-processes
    handleNestedSubProcesses(nodes, created, diMap, injector);
  } finally {
    canvas.setRootElement(previousRoot);
  }
}

/**
 * For each collapsed sub-process among the created shapes, create a plane
 * and recursively import its children.
 */
function handleNestedSubProcesses(nodes, createdShapes, diMap, injector) {
  const canvas = injector.get('canvas');
  const bpmnFactory = injector.get('bpmnFactory');
  const elementFactory = injector.get('elementFactory');

  for (const flowEl of nodes) {
    const shape = createdShapes[flowEl.id];
    if (!shape || !is(flowEl, 'bpmn:SubProcess')) continue;
    if (isExpanded(shape) || !flowEl.flowElements?.length) continue;

    const nestedPlane = ensureSubProcessPlane(shape, canvas, bpmnFactory, elementFactory);
    if (nestedPlane) {
      importElementsToPlane(flowEl.flowElements, flowEl, nestedPlane, diMap, injector);
    }
  }
}


// ---------------------------------------------------------------------------
// Shape / connection creation
// ---------------------------------------------------------------------------

function createShapes(nodes, plane, diMap, canvas, elementRegistry) {
  const created = {};

  for (const flowEl of nodes) {
    const di = diMap.get(flowEl);
    if (!di?.bounds) continue;
    if (elementRegistry.get(flowEl.id)) continue;

    const attrs = {
      id: flowEl.id,
      type: flowEl.$type,
      businessObject: flowEl,
      di,
      x: di.bounds.x,
      y: di.bounds.y,
      width: di.bounds.width,
      height: di.bounds.height,
      incoming: [],
      outgoing: []
    };

    if (is(flowEl, 'bpmn:SubProcess')) {
      attrs.isExpanded = di.isExpanded ?? false;
    }

    try {
      created[flowEl.id] = canvas.addShape(attrs, plane);
    } catch (error_) {
      console.warn('[SubProcessFromFile] Shape error:', flowEl.id, error_);
    }
  }

  return created;
}

function createConnections(connections, plane, diMap, createdShapes, canvas, elementRegistry) {
  for (const flowEl of connections) {
    if (!flowEl.sourceRef || !flowEl.targetRef) continue;

    const source = createdShapes[flowEl.sourceRef.id] || elementRegistry.get(flowEl.sourceRef.id);
    const target = createdShapes[flowEl.targetRef.id] || elementRegistry.get(flowEl.targetRef.id);
    if (!source || !target) continue;

    const di = diMap.get(flowEl);
    const waypoints = di?.waypoint?.map(wp => ({ x: wp.x, y: wp.y })) || [];

    try {
      const conn = canvas.addConnection({
        id: flowEl.id,
        type: flowEl.$type,
        businessObject: flowEl,
        di,
        source,
        target,
        waypoints
      }, plane);

      // Wire incoming/outgoing so diagram-js can resolve references
      source.outgoing.push(conn);
      target.incoming.push(conn);
    } catch (error_) {
      console.warn('[SubProcessFromFile] Connection error:', flowEl.id, error_);
    }
  }
}


// ---------------------------------------------------------------------------
// Semantic model (BO) helpers
// ---------------------------------------------------------------------------

function attachToParentBO(flowElements, parentBO) {
  if (!parentBO.flowElements) parentBO.flowElements = [];

  for (const el of flowElements) {
    el.$parent = parentBO;
    if (!parentBO.flowElements.includes(el)) {
      parentBO.flowElements.push(el);
    }
  }
}


// ---------------------------------------------------------------------------
// DI (diagram interchange) helpers
// ---------------------------------------------------------------------------

/**
 * Build a Map<moddleElement, diElement> from ALL diagrams / planes in the
 * parsed definitions.  Keyed by object reference so it survives ID remapping.
 */
function buildDiMap(definitions) {
  const map = new Map();
  for (const diagram of (definitions.diagrams || [])) {
    for (const pe of (diagram.plane?.planeElement || [])) {
      if (pe.bpmnElement) {
        map.set(pe.bpmnElement, pe);
      }
    }
  }
  return map;
}

/**
 * Copy the imported DI elements (BPMNShape / BPMNEdge) into the target
 * BPMNPlane that corresponds to `parentBO`.
 */
function attachDiToPlane(nodes, connections, diMap, canvas, parentBO) {
  const diagram = findDiagramForBO(canvas, parentBO);
  if (!diagram?.plane) return;

  if (!diagram.plane.planeElement) diagram.plane.planeElement = [];

  for (const el of [...nodes, ...connections]) {
    const di = diMap.get(el);
    if (di) {
      di.$parent = diagram.plane;
      diagram.plane.planeElement.push(di);
    }
  }
}

function findDiagramForBO(canvas, businessObject) {
  const definitions = getDefinitions(canvas);
  return definitions?.diagrams?.find(d => d.plane?.bpmnElement === businessObject) ?? null;
}


// ---------------------------------------------------------------------------
// Plane management
// ---------------------------------------------------------------------------

/**
 * Ensure a BPMNDiagram + BPMNPlane + canvas root element exist for a
 * collapsed sub-process shape.  If already present, return the existing plane.
 */
function ensureSubProcessPlane(shape, canvas, bpmnFactory, elementFactory) {
  const planeId = shape.id + '_plane';
  const existing = canvas.getRootElements().find(r => r.id === planeId);
  if (existing) return existing;

  const bo = shape.businessObject;

  // Create DI structures
  const diPlane = bpmnFactory.create('bpmndi:BPMNPlane', { bpmnElement: bo });
  const diDiagram = bpmnFactory.create('bpmndi:BPMNDiagram', { plane: diPlane });
  diPlane.$parent = diDiagram;

  const definitions = getDefinitions(canvas);
  if (definitions) {
    definitions.diagrams.push(diDiagram);
  }

  // Create canvas root element
  const planeElement = elementFactory.createRoot({
    id: planeId,
    type: bo.$type,
    di: diPlane,
    businessObject: bo,
    collapsed: true
  });

  canvas.addRootElement(planeElement);
  return planeElement;
}


// ---------------------------------------------------------------------------
// Generic utilities
// ---------------------------------------------------------------------------

function getDefinitions(canvas) {
  try {
    for (const root of canvas.getRootElements()) {
      const defs = root.businessObject?.$parent;
      if (defs?.$type === 'bpmn:Definitions') return defs;
    }
  } catch (error_) {
    console.debug('[SubProcessFromFile] Canvas not ready:', error_);
  }
  return null;
}

/**
 * Locate the first bpmn:Process in the parsed definitions (also looks
 * inside bpmn:Collaboration → participants).
 */
function findSourceProcess(definitions) {
  for (const el of definitions.rootElements || []) {
    if (el.$type === 'bpmn:Process') return el;
  }
  for (const el of definitions.rootElements || []) {
    if (el.$type === 'bpmn:Collaboration') {
      for (const p of el.participants || []) {
        if (p.processRef) return p.processRef;
      }
    }
  }
  return null;
}

/**
 * Remap IDs of imported flow elements (recursively) where they conflict
 * with elements already present in the diagram.
 */
/**
 * IDs der zu importierenden Elemente umbenennen, wo sie schon vergeben sind.
 *
 * `belegt` muss **alle** IDs der Zieldatei enthalten, nicht nur die der
 * Elementregistrierung: Ein Baustein steht nicht auf der Zeichenflaeche, seine
 * Elemente fehlen dort also — und eine Kopie schrieb jede seiner IDs ein
 * zweites Mal in die Datei (gemessen: 88 Dubletten bei einem Baustein mit 77
 * Elementen). Doppelte IDs sind in BPMN unzulaessig, und moddle loest
 * Verweise darueber auf.
 *
 * Steigt vollstaendig ab, nicht nur eine Ebene tief, und benennt das DI mit —
 * auch dessen IDs waeren sonst doppelt.
 */
function remapIds(flowElements, belegt, diMap) {
  let counter = 1;

  const frei = (basis) => {
    let neu;
    do {
      neu = basis + '_imp_' + counter++;
    } while (belegt.has(neu));
    return neu;
  };

  const remap = (el) => {
    if (!el?.id) return;
    if (belegt.has(el.id)) {
      const neu = frei(el.id);
      el.id = neu;
      belegt.add(neu);
    } else {
      belegt.add(el.id);
    }
    // Das zugehoerige DI traegt eine eigene ID und kollidiert genauso.
    const di = diMap?.get(el);
    if (di?.id) {
      if (belegt.has(di.id)) {
        const neu = frei(di.id);
        di.id = neu;
        belegt.add(neu);
      } else {
        belegt.add(di.id);
      }
    }
    for (const kind of el.flowElements || []) remap(kind);
    for (const kind of el.artifacts || []) remap(kind);
  };

  for (const el of flowElements) remap(el);
}

/**
 * Check whether the target element should receive the "from file" entry.
 * Mirrors the condition used by the built-in ReplaceMenuProvider for TASK entries.
 */
export function isApplicable(element) {
  const bo = element.businessObject || element;

  if (!is(bo, 'bpmn:FlowNode')) return false;
  if (is(bo, 'bpmn:StartEvent') || is(bo, 'bpmn:EndEvent')) return false;
  if (is(bo, 'bpmn:BoundaryEvent')) return false;
  if (is(bo, 'bpmn:IntermediateCatchEvent') || is(bo, 'bpmn:IntermediateThrowEvent')) return false;
  if (is(bo, 'bpmn:Gateway')) return false;
  if (is(bo, 'bpmn:SubProcess') && (isExpanded(element) || bo.triggeredByEvent)) return false;
  if (is(bo, 'bpmn:Transaction')) return false;

  return true;
}

/**
 * Insert `newEntry` into `entries` right after `afterKey`.
 * Falls back to appending if `afterKey` is not found.
 */
export function insertAfterKey(entries, afterKey, newKey, newEntry) {
  const result = {};
  let inserted = false;

  for (const key of Object.keys(entries)) {
    result[key] = entries[key];
    if (key === afterKey && !inserted) {
      result[newKey] = newEntry;
      inserted = true;
    }
  }
  if (!inserted) result[newKey] = newEntry;
  return result;
}

function removeExtension(name) {
  if (!name) return 'Sub-process';
  return name.replace(/\.(bpmn|xml)$/i, '');
}


// ---------------------------------------------------------------------------
// Auto-colour helpers (for imported sub-process elements)
// ---------------------------------------------------------------------------

const DEFAULT_COLORS = [
  { check: 'bpmn:Task',               fill: '#FFE0B2', stroke: '#6B3C00' },
  { check: 'bpmn:DataObjectReference', fill: '#FFF9C4', stroke: '#6D5E00' },
];
const IMPORTED_SUB_PROCESS_COLOR = { fill: '#BBDEFB', stroke: '#0D4372' };

/** Check (recursively) whether any imported element carried a colour. */
function hasAnyColor(flowElements, diMap) {
  for (const el of flowElements) {
    const di = diMap.get(el);
    if (di && (di.get?.('background-color') || di.get?.('bioc:fill'))) {
      return true;
    }
    if (el.flowElements && hasAnyColor(el.flowElements, diMap)) {
      return true;
    }
  }
  return false;
}

/** Colour a single imported element according to default rules. */
function colorSingleElement(el, shape, modeling) {
  if (is(el, 'bpmn:SubProcess') && !isExpanded(shape)) {
    modeling.setColor(shape, IMPORTED_SUB_PROCESS_COLOR);
    return;
  }
  for (const def of DEFAULT_COLORS) {
    if (is(el, def.check)) {
      modeling.setColor(shape, { fill: def.fill, stroke: def.stroke });
      break;
    }
  }
}

/** Apply default colours to all eligible imported elements (recursively). */
function applyDefaultColors(flowElements, elementRegistry, modeling) {
  for (const el of flowElements) {
    const shape = elementRegistry.get(el.id);
    if (shape) {
      colorSingleElement(el, shape, modeling);
    }
    if (el.flowElements) {
      applyDefaultColors(el.flowElements, elementRegistry, modeling);
    }
  }
}
